import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { actions as termActions, selectors as termSelectors } from '../../reducers/terms';
import Loading from '../Loading';
import compact from 'lodash/compact';
import get from 'lodash/get';
import partition from 'lodash/partition';
import { PricingGridWrapper } from '@cimpress-technology/pricing-grid';
import { auth } from '../../utils/auth';
import { isProduction } from '../../utilities/processFunctions';
import { IsProductTermType, TermType } from '../../constants/termTypes';
import { getRuleSet } from '../../services/SkuRuleset';
import PricingGrid from '@cimpress-technology/pricing-grid-ts';
import UnionSelector from '@cimpress-technology/union-selector';
import cloneDeep from 'lodash/cloneDeep';

/**
 * This component is a pricing grid with multiple columns.
 */
class TermsPricingGrid extends Component {
  static defaultProps = {};

  static propTypes = {
    // TODO: Move sku into termRevisionsAndLabels object when the PricingGrid component supports multiple skus.
    sku: PropTypes.string.isRequired,
    termRevisionsAndLabels: PropTypes.arrayOf(
      PropTypes.shape({
        termRevision: PropTypes.object.isRequired,
        label: PropTypes.node
      })
    ).isRequired
  };

  state = {
    loaded: false,
    loading: false,
    loadFailed: false
  };

  componentWillMount() {
    const { termRevisionsAndLabels, fetchTermRevisionItemBySku, sku } = this.props;
    this.setState({ loading: true });
    const promises = termRevisionsAndLabels.map(({ termRevision }) =>
      fetchTermRevisionItemBySku(termRevision.termId, termRevision.revisionId, sku)
    );
    getRuleSet(sku)
      .then(skuRuleSets => {
        const authoritativeRuleSet = skuRuleSets.filter(each => each.authoritative);
        if (authoritativeRuleSet) {
          const skuRuleSet = authoritativeRuleSet[0];
          this.setState({ skuRuleSet });
          this.props.clearSkuRuleSetNotFoundError(null, sku);
        } else {
        }
      })
      .catch(error => {
        this.props.showSkuRulesetNotFoundError(error, sku);
      });

    Promise.all(promises)
      .then(_ => this.setState({ loading: false, loaded: true }))
      .catch(_ => this.setState({ loading: false, loaded: false, loadFailed: true }));
  }

  getPreview(termRevision, priceModelInfo, columnTitle) {
    if (!termRevision || (IsProductTermType(termRevision.type) && !priceModelInfo)) {
      return null;
    }

    const discountPercent = get(termRevision, 'modifiers.discountPercent', null);
    const markupPercent = get(termRevision, 'modifiers.markupPercent', null);
    const destinationCountries = get(termRevision, 'destinationCountries', []);
    const priceModelId = get(priceModelInfo, 'id');
    const priceModelRevisionId = get(priceModelInfo, 'revisionId');

    return {
      title: columnTitle || get(termRevision, 'displayAgreementType', '') || 'Contract Price',
      priceModelId,
      revisionId: priceModelRevisionId,
      discountPercent,
      markupPercent,
      destinationCountries: destinationCountries.length > 0 ? destinationCountries : undefined,
      isFreeShipping: termRevision.type === TermType.FREE_SHIPPING
    };
  }
  formProductPriceContractsInfo = (productTermsAndLabels, columnTitle, sku) => {
    const termRevisions = [];
    productTermsAndLabels.forEach(termObject => {
      const { termRevision } = termObject;
      columnTitle = termObject.label.props
        ? termObject.label.props.children
          ? termObject.label.props.children.join('')
          : null
        : null;
      const skuInfo = this.props.getItem(termRevision.termId, termRevision.revisionId, sku) || {};
      const revision = {
        buyer: {
          buyerType: get(termRevision, 'buyer.type'),
          buyerId: get(termRevision, 'buyer.id')
        },
        seller: {
          sellerType: get(termRevision, 'seller.type'),
          sellerId: get(termRevision, 'seller.id')
        },
        priceModelId: get(skuInfo, 'priceModel.id'),
        revisionId: get(skuInfo, 'priceModel.revisionId'),
        effectiveStartDate: get(termRevision, 'effectiveStartDate', null),
        contractId: get(termRevision, 'termId'),
        contractRevisionId: get(termRevision, 'revisionId'),
        modifiers: {
          discountPercent: get(termRevision, 'modifiers.discountPercent', null)
        },
        title: columnTitle || get(termRevision, 'displayAgreementType', '') || 'Contract Price'
      };
      termRevisions.push(revision);
    });
    return termRevisions;
  };

  onChange = productState => {
    const { sku } = this.props;
    let newProductState = {};

    if (productState[sku]) {
      newProductState[sku] = productState[sku];
    }
    const newProductStateValues = Object.values(newProductState);

    if (newProductStateValues && newProductStateValues.length > 0) {
      const { displayedAttributes } = newProductStateValues[0];
      this.setState({
        productState: newProductState,
        initialDisplayedAttributes: displayedAttributes
      });
    } else {
      this.setState({
        productState: newProductState,
        initialDisplayedAttributes: {}
      });
    }
  };

  onError = (errormessage, x) => {
    console.log(errormessage);
  };
  render() {
    const { termRevisionsAndLabels, sku, getItem, requiredInputs } = this.props;
    const { loading, loadFailed, skuRuleSet, productState } = this.state;

    let skuData = {};
    if (
      productState &&
      productState[sku] &&
      productState[sku].selectedRow &&
      termRevisionsAndLabels &&
      termRevisionsAndLabels[0] &&
      termRevisionsAndLabels[0].termRevision &&
      termRevisionsAndLabels[0].termRevision.seller
    ) {
      skuData = {};
      skuData[sku] = cloneDeep(productState[sku]) || {}; // use deep copy
      skuData[sku] = {
        ...skuData[sku],
        sellerAccountId: termRevisionsAndLabels[0].termRevision.seller.accountId,
        sellerId: termRevisionsAndLabels[0].termRevision.seller.id
      };
    }
    // TODO: Once skus are part of the termRevisionsAndLabels object, use the seller from termRevision there instead of extracting from the first termRevision.
    const { type: sellerType, id: sellerId } = get(termRevisionsAndLabels, '[0].termRevision.seller', {});

    if (loading) {
      return <Loading />;
    }

    if (loadFailed) {
      return <div>Something went wrong while fetching items</div>;
    }

    const [productTermsAndLabels, shippingTermsAndLabels] = partition(termRevisionsAndLabels, ({ termRevision }) =>
      IsProductTermType(termRevision.type)
    );
    const getPreviewsFor = termsAndLabels =>
      compact(
        termsAndLabels.map(({ termRevision, label }) => {
          const item = getItem(termRevision.termId, termRevision.revisionId, sku) || {};
          return this.getPreview(termRevision, item.priceModel, label);
        })
      );

    const productPricePreviews = getPreviewsFor(productTermsAndLabels);
    const shippingPricePreviews = getPreviewsFor(shippingTermsAndLabels);

    let gridToShow = <div> </div>;
    if (shippingPricePreviews && shippingPricePreviews.length > 0) {
      gridToShow = (
        <div>
          <PricingGridWrapper
            sku={sku}
            onGetAccessToken={auth.getAccessToken}
            sellerId={sellerId}
            sellerType={sellerType}
            isProduction={isProduction}
            productPricePreviews={productPricePreviews}
            //TODO: Only supports 1 shipping cost preview at the moment. Change the wrapper component to support multiple
            shippingCostPreview={shippingPricePreviews.length && shippingPricePreviews[0]}
          />
        </div>
      );
    } else if (skuRuleSet && requiredInputs) {
      const productPriceContractsInfo = this.formProductPriceContractsInfo(productTermsAndLabels, null, sku);
      gridToShow = (
        <div>
          <div className={`col-xs-12 col-md-5`}>
            <UnionSelector
              productId={skuRuleSet.referenceId}
              showErrorModal
              authToken={auth.getAccessToken()}
              consumerId="price"
              showPivotConfig={false}
              onChange={this.onChange}
              showCompetitivePricing={false}
              competitorProducts={[]}
              onError={this.onError}
              requiredAttributes={requiredInputs[skuRuleSet.referenceId]}
            />
          </div>
          <div className={`col-xs-12 col-md-7`}>
            <PricingGrid
              skuData={skuData}
              onGetAccessToken={auth.getAccessToken}
              isProduction={isProduction}
              parentService="contracts"
              widthOfGrid="100%"
              productPriceContractsInfo={productPriceContractsInfo}
            />
          </div>
        </div>
      );
    }
    return gridToShow;
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    requiredInputs: termSelectors.getTermItemsRequiredInputs(state),
    getItem: (termId, revisionId, sku) => termSelectors.getTermRevisionItemBySku(state, termId, revisionId, sku)
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  return {
    fetchTermRevisionItemBySku: (termId, revisionId, sku) =>
      dispatch(termActions.fetchTermRevisionItemBySku(termId, revisionId, sku)),
    showSkuRulesetNotFoundError: (error, skuInfo) =>
      dispatch(termActions.setSkuRuleSetLoadingStatus(skuInfo, true, error)),
    clearSkuRuleSetNotFoundError: skuInfo => dispatch(termActions.setSkuRuleSetLoadingStatus(skuInfo, false, null))
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(TermsPricingGrid);
